import { useEffect } from "react";
import { Token, User } from "src/models/User";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { useRouter } from "next/navigation";

export const AUTH_API_HOST =
  process.env.NEXT_PUBLIC_ENV !== "production"
    ? "https://dev-auth.bhaptics.com"
    : "https://auth.bhaptics.com";

export async function fetchUser() {
  const res = await fetch(`${AUTH_API_HOST}/api/user`, {
    credentials: "include",
  });
  if (res.ok) {
    // 비로그인 세션이면 auth 가 200 + 빈 body 를 준다 (dev/prod 동일 실측).
    // 여기서 res.json() 이 throw 하면 쿼리가 data 없는 error 로 남고, react-query 는
    // error 쿼리를 리마운트 refetch 때 pending 으로 되돌린다 → PDP 결제 버튼이
    // 스크롤(하단 바 리마운트)마다 로딩 폴백(단일 결제 버튼)으로 깜빡인다.
    // 게스트는 정상 상태이므로 throw 대신 null(성공)로 캐시한다.
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as User;
    } catch {
      // 200 인데 JSON 도 아니면(프록시 에러 페이지 등) 게스트로 간주하되 흔적은 남긴다.
      console.warn("/api/user returned non-JSON body; treating as guest");
      return null;
    }
  }
  return null;
}

/**
 * "로그인 완료" 단일 술어 — 세션이 있고 **약관 동의까지 끝난** 상태.
 *
 * auth 의 OTP 는 로그인+가입 통합이라 미가입 이메일도 즉석 가입되며 `termsAgreement=false`
 * 로 돌아온다. 그 상태는 auth 쪽 동의 화면이 남은 것이므로 완료로 보면 안 된다.
 * (레거시 세션은 필드 자체가 없으므로 `=== false` 만 미완료로 판정한다.)
 *
 * ⚠️ 현재 사용처는 **팝업 로그인 감시**(`useAuthPopup`) 한 곳이다. PDP 게이트
 * (`useIsSplitCheckout` = `!isLoading && !user`)는 여전히 세션 유무만 본다 — 즉
 * `termsAgreement === false` 사용자는 게이트 기준 "로그인"이라 모달 없이 바로 결제로 간다.
 * 그 경로는 세션·토큰이 실재하므로 주문 귀속은 정상이고, 게이트를 이 술어로 바꾸면
 * #5293 의 단일/분리 버튼 분기가 함께 움직여서 이 PR 범위를 넘는다. **의도적으로 남긴 갈림**이며,
 * 새 소비처를 추가할 때는 어느 축(세션 유무 / 동의 완료)이 필요한지 명시할 것.
 */
export function isSignedInComplete(user: User | null | undefined): boolean {
  return !!user?.isLoggedIn && user.termsAgreement !== false;
}

/**
 * `/api/token` 1회 조회 결과. **"비로그인 확정(401)"과 "일시 실패"를 구분**하는 것이 핵심 —
 * 둘을 null 로 뭉개면 일시 장애 때 로그인 사용자의 주문이 조용히 게스트 주문이 된다.
 */
type TokenFetchResult =
  | { status: "ok"; token: Token }
  | { status: "unauthenticated" }
  | { status: "error"; reason: string };

/**
 * 상한은 **경로별로 다르다.**
 *
 * - 클릭 경로(`resolveCheckoutToken`)는 짧게 — 결제 버튼이 auth 왕복에 묶이므로 hang 을
 *   그대로 두면 스피너가 영구화된다(docs/pitfalls.md #19 — 결제-critical 버튼을 백엔드
 *   호출에 gate 하면 hang = 영구 잠김).
 * - 배경 쿼리(`fetchToken` = react-query)는 길게 — 여기서 짧게 끊으면 **느린 회선에서
 *   토큰이 계속 null 이 되어** 로그인 사용자가 게스트로 판정된다. 종전에는 느려도 결국
 *   성공했다. 두 경로가 같은 상한을 쓰면 그 회귀가 조용히 생긴다.
 */
const TOKEN_TIMEOUT_CHECKOUT_MS = 3000;
const TOKEN_TIMEOUT_BACKGROUND_MS = 15000;

/**
 * `AbortSignal.timeout` 은 Safari 16 미만·일부 테스트 런타임(jsdom)에 없다. 그냥 부르면
 * **모든 토큰 요청이 예외로 떨어져** 항상 캐시 폴백/게스트가 된다 — 없는 환경에서는
 * 타임아웃만 포기하고 요청 자체는 정상 수행한다.
 */
function timeoutSignal(ms: number): AbortSignal | undefined {
  if (typeof AbortSignal === "undefined") return undefined;
  if (typeof AbortSignal.timeout !== "function") return undefined;
  return AbortSignal.timeout(ms);
}

async function requestToken(timeoutMs: number): Promise<TokenFetchResult> {
  let res: Response;
  try {
    res = await fetch(`${AUTH_API_HOST}/api/token`, {
      credentials: "include",
      signal: timeoutSignal(timeoutMs),
    });
  } catch {
    // 네트워크/CORS 실패는 **throw 하지 않는다** — 호출처(체크아웃 클릭 핸들러)가
    // reject 되면 로딩 상태가 풀리지 않아 버튼이 영구 잠긴다.
    return { status: "error", reason: "network" };
  }
  if (res.status === 401) return { status: "unauthenticated" };
  if (!res.ok) return { status: "error", reason: `http_${res.status}` };
  try {
    return { status: "ok", token: (await res.json()) as Token };
  } catch {
    // 200 인데 JSON 이 아니면(프록시 에러 페이지 등) 실패로 본다. `fetchUser` 와 같은 이유로
    // 흔적을 남긴다 — 여기서 조용히 게스트로 떨어지면 결제 귀속이 틀어진다.
    console.warn("/api/token returned non-JSON body");
    return { status: "error", reason: "non_json" };
  }
}

/**
 * react-query 용.
 *
 * ⚠️ **일시 실패는 반드시 throw 한다.** 종전 queryFn 은 `await fetch` 가 try 밖이라
 * 네트워크 실패에 throw 했고, 그래서 (1) 기본 retry 3회가 돌고 (2) 실패해도 `data` 가
 * **마지막 성공값을 유지**했다. 여기서 null 을 반환하면 쿼리가 success+data=null 이 되어
 * 재시도가 사라지고 **캐시의 유효 토큰이 null 로 덮인다** — 그런데 그 캐시가 바로
 * `resolveCheckoutToken` 의 폴백 대상이라, 배경 refetch 한 번이 폴백을 무력화하고
 * 회원 주문을 게스트로 만든다.
 *
 * 401(비로그인 확정)만 `null` = 정상 상태다(게스트는 에러가 아니다).
 */
export async function fetchToken(): Promise<Token | null> {
  const result = await requestToken(TOKEN_TIMEOUT_BACKGROUND_MS);
  if (result.status === "ok") return result.token;
  if (result.status === "unauthenticated") return null;
  throw new Error(`/api/token unavailable: ${result.reason}`);
}

/**
 * 행동 직전의 토큰 해석 — 처음 쓴 자리가 체크아웃이라 이름이 그렇다. 소비처: 결제
 * (`useCheckout` · 카트 자체 결제 `cart-checkout-button` · 네이버페이 `NaverPayButton`) · 게임 지원 요청 투표
 * (`game-request-api`) · 쿠폰 착지 이동(`pages/coupon/claim.tsx`).
 *
 * 실행 시점에 새로 읽는 이유: 클릭 → (로그인 모달) → 진행 흐름에서 훅의 token 을 클로저로
 * 잡으면 모달에서 로그인한 뒤에도 **클릭 시점의 게스트 값**으로 주문이 만들어진다.
 *
 * 다만 fresh fetch 는 종전 react-query(재시도 + 마지막 성공값 유지)가 흡수하던 일시 장애에
 * 그대로 노출된다. 그래서 **401(비로그인 확정)일 때만 게스트로 확정**하고, 그 외 실패는
 * 캐시된 토큰으로 폴백해 종전 동작과 맞춘다. 캐시도 없으면 게스트(= 종전과 동일).
 *
 * ⚠️ **캐시를 `null` 로 쓰는 건 401 뿐이다** — 일시 실패는 캐시를 건드리지 않는다. 쿠폰 착지는
 * 이 함수가 끝난 뒤의 캐시 값으로 비로그인(`null`)과 모름(`undefined`)을 가른다. 일시 실패에
 * `null` 을 쓰게 바꾸면 그 페이지가 로그인 사용자를 로그인 화면으로 보낸다
 * (`__tests__/coupon/claim-page-token-states.test.tsx` 가 잠근다).
 */
export async function resolveCheckoutToken(
  queryClient: QueryClient,
): Promise<string | undefined> {
  const result = await requestToken(TOKEN_TIMEOUT_CHECKOUT_MS);

  if (result.status === "ok") {
    queryClient.setQueryData(["/api/token"], result.token);
    return result.token.token;
  }
  if (result.status === "unauthenticated") {
    queryClient.setQueryData(["/api/token"], null);
    return undefined;
  }

  const cached = queryClient.getQueryData<Token | null>(["/api/token"]);
  if (cached?.token) {
    console.warn(
      `/api/token 일시 실패(${result.reason}) — 캐시 토큰으로 폴백 (게스트 주문 방지)`,
    );
    return cached.token;
  }
  console.warn(`/api/token 조회 실패(${result.reason}) — 게스트로 진행`);
  return undefined;
}

/**
 * 토큰 캐시가 **비로그인 확정(401)** 인가.
 *
 * 캐시를 `null` 로 쓰는 곳은 둘뿐이다 — 배경 쿼리 `fetchToken`(401 → `null` 반환)과 위
 * `resolveCheckoutToken` 의 401 분기. 일시 실패는 둘 다 캐시를 **건드리지 않는다**
 * (`fetchToken` 은 throw, `resolveCheckoutToken` 은 폴백만 한다). 그래서 남는 것은
 * `undefined`(받은 적 없음)나 직전 값이고, 그걸 로그아웃으로 읽으면 안 된다 — `null` 만 본다.
 *
 * `resolveCheckoutToken` 이 토큰을 못 줬을 때 「세션이 끝났나 / 일시 장애인가」를 이걸로
 * 가른다(`use-game-request-vote.ts` — 앞의 것은 로그인으로, 뒤의 것은 새로고침 안내).
 */
export function isConfirmedGuest(queryClient: QueryClient): boolean {
  return queryClient.getQueryData(["/api/token"]) === null;
}

export default function useUser({
  redirectTo = "",
  redirectIfFound = false,
} = {}) {
  const router = useRouter();

  // ⚠️ `isLoading` 은 이름과 달리 react-query v5 의 `isLoading`(= `isPending && isFetching`)
  // 이 아니라 **`isPending`** 이다. 호출부가 오래 이 이름을 써서 별칭으로 남겼다.
  // 둘이 갈리는 지점이 실제로 있다 — **`enabled: false` 를 붙이면 `isPending` 이 영구히
  // `true`** 다. 이 쿼리에 게이트를 붙이면 결제 버튼이 영원히 "인증 확인 중" 으로 굳는다.
  // `__tests__/api/use-user-contract.test.ts` 가 그 전제를 잠근다.
  //
  // `isError` 도 함께 내보낸다: `isPending === false` 인데 `user === undefined` 인 상태가
  // 존재하고(네트워크 실패 — 아래 `fetchUser` 의 `await fetch` 는 try 밖이다),
  // 그때 "게스트로 확정" 해버리면 회원에게 가입 유도가 뜬다.
  const {
    data: user,
    isPending: isLoading,
    isError,
  } = useQuery({
    queryKey: ["/api/user"],
    queryFn: fetchUser,
  });
  const { data: token, isPending: isTokenLoading } = useQuery({
    queryKey: ["/api/token"],
    refetchInterval: 50 * 60 * 1000,
    refetchOnWindowFocus: "always",
    queryFn: fetchToken,
  });

  useEffect(() => {
    // if no redirect needed, just return (example: already on /dashboard)
    // if user data not yet there (fetch in progress, logged in or not) then don't do anything yet
    if (!redirectTo || !user) return;

    if (
      // If redirectTo is set, redirect if the user was not found.
      (redirectTo && !redirectIfFound && !user?.isLoggedIn) ||
      // If redirectIfFound is also set, redirect if the user was found
      (redirectIfFound && user?.isLoggedIn)
    ) {
      router.push(redirectTo);
    }
  }, [user, redirectIfFound, redirectTo]);

  return { user, isLoading, isError, isTokenLoading, token };
}

/**
 * 서버쪽에서는 window 정보가 없어서 successUrl 제공.
 *
 * ## ★`encodeURIComponent` 여야 한다 — `encodeURI` 는 `? & =` 를 그대로 둔다
 *
 * 종전엔 `encodeURI` 였다. 그러면 복귀 URL 에 파라미터가 **둘 이상**일 때 두 번째부터가
 * **로그인 페이지 자신의 파라미터**가 되어 유실된다. 실측(2026-09-08 · auth 응답의
 * 파싱된 상태를 읽음 · 복귀 URL `…/games/vr/games/?device=Arm&q=ZQMARK`):
 *
 * ```
 *                        success-url 로 파싱된 값                    상위 키 누출
 *   encodeURI            …/games/vr/games/?device=Arm               q=ZQMARK  ★유실
 *   encodeURIComponent   …/games/vr/games/?device=Arm&q=ZQMARK      없음
 * ```
 *
 * `dev-auth` · `auth`(prod) × `/login` · `/logout` **4조합 전부** 같았다 — auth 는
 * 퍼센트 인코딩된 값을 한 번 디코드해 읽는다. 같은 리포의 `useAuthEmbed`·`useAuthPopup`
 * 이 이미 `url.searchParams.set` 으로 그렇게 보내고 있고 그 경로가 운영에서 동작한다.
 *
 * ⚠️ 종전 제약(*"파라미터는 1개까지만 넣을 것"*)을 전제로 쓰인 자리가 있었다 —
 * `AuthUtils.buildAuthUrl` KDoc · `couponProductPath` · `BuyTactsuitDetailPageTemplate`.
 * 이 수정으로 그 제약이 사라져 세 자리의 주석을 함께 걷었다. 되돌리면 그 자리들이
 * 조용히 거짓이 되므로 `__tests__/api/auth-link-encoding.test.ts` 가 4자리를 잠근다.
 */
export const generateLoginLink = (successUrl: string) => {
  return `${AUTH_API_HOST}/login?success-url=${encodeURIComponent(successUrl)}`;
};

/** 위 `generateLoginLink` 와 같은 이유로 `encodeURIComponent` 다. */
export const generateLogoutLink = (successUrl: string) => {
  return `${AUTH_API_HOST}/logout?redirect-url=${encodeURIComponent(successUrl)}`;
};
